<script setup lang="ts">
import { computed } from "vue";

const { message, type = "warning" } = defineProps<{
  message: string;
  type?: "success" | "warning" | "error" | "info";
}>();

const emit = defineEmits<{
  close: [];
}>();

const toastClass = computed(() => {
  const classMap = {
    success: "base-toast-success",
    warning: "base-toast-warning",
    error: "base-toast-error",
    info: "base-toast-info"
  };

  return classMap[type];
});
</script>

<template>
  <div :class="['base-toast', toastClass]" role="alert" aria-live="polite">
    <div class="base-toast-content">
      <span class="base-toast-message">{{ message }}</span>
      <button class="base-toast-close" aria-label="Close notification" @click="() => emit('close')">✕</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-toast {
  position: relative;
  left: 50%;
  width: fit-content;
  min-width: 320px;
  max-width: 700px;
  transform: translateX(-50%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 4px 10px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  backdrop-filter: blur(10px);
}

.base-toast-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}

.base-toast-message {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
}

.base-toast-close {
  flex-shrink: 0;
  padding: 0.25rem;
  transition: all 0.2s ease;
  border: none;
  border-radius: 4px;
  opacity: 0.7;
  background: none;
  font-size: 1.1rem;
  cursor: pointer;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
}

.base-toast-warning {
  background: rgba(217, 119, 6, 0.95);
  color: #fff;

  .base-toast-icon {
    color: #fed7aa;
  }

  .base-toast-close {
    color: #fff;
  }
}

.base-toast-error {
  background: rgba(220, 38, 38, 0.95);
  color: #fff;

  .base-toast-icon {
    color: #fecaca;
  }

  .base-toast-close {
    color: #fff;
  }
}

.base-toast-success {
  background: rgba(5, 150, 105, 0.95);
  color: #fff;

  .base-toast-icon {
    color: #bbf7d0;
  }

  .base-toast-close {
    color: #fff;
  }
}

.base-toast-info {
  background: rgba(49, 102, 147, 0.95);
  color: #fff;

  .base-toast-icon {
    color: #bfdbfe;
  }

  .base-toast-close {
    color: #fff;
  }
}
</style>
